import {SketchRelationKind} from '../solver/constraint/relation-compatibility/SketchRelationKinds';
import type {SketchRelationDefinition} from '../solver/constraint/relationDefinition/SketchRelationDefinition';
import type {ISketchObject} from '../../mesh/sketch/ISketchObject';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {SketchMirrorAxisResolvedGeometry} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';

/**
 * 草图几何变换关系策略。
 *
 * 职责边界：
 * 1. 只根据几何变换判断关系能否保持、需要迁移为何种等价关系或必须删除；
 * 2. 不修改约束容器、不执行几何变换，也不管理历史事务；
 * 3. 原位反射时 Fix/FixSlot 由变换后的当前几何定义新锚点，可以继续保留；复制反射时源固定关系
 *    仍留在源实体上，镜像体由对称关系驱动，不重复克隆绝对固定关系；
 * 4. 多目标相对关系不在此策略中删除。
 */
export class SketchGeometryTransformConstraintPolicy {
    /** 方向分类容差。 */
    private static readonly DIRECTION_EPSILON = 1e-8;

    /**
     * 解析单目标关系经过镜像反射后的等价关系类型。
     *
     * @param constraint 源实体约束。
     * @param referencePlane 当前草图参考面。
     * @param axisGeometry 本次镜像轴世界几何。
     * @returns 原关系、等价迁移关系；不存在等价绝对关系时返回 null。
     */
    public resolveReflectedRelationKind(
        constraint: SketchRelationDefinition<ISketchObject>,
        referencePlane: ReferencePlane,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): SketchRelationKind | null {
        if (
            constraint.getTargetsSnapshot().length !== 1
            || (
                constraint.relationType !== SketchRelationKind.Horizontal
                && constraint.relationType !== SketchRelationKind.Vertical
            )
        ) {
            return constraint.relationType;
        }

        const axisStartLocal = referencePlane.worldToLocal(axisGeometry.startWorld);
        const axisEndLocal = referencePlane.worldToLocal(axisGeometry.endWorld);
        const axisDeltaX = axisEndLocal.x - axisStartLocal.x;
        const axisDeltaY = axisEndLocal.y - axisStartLocal.y;
        const axisLength = Math.hypot(axisDeltaX, axisDeltaY);
        if (axisLength <= SketchGeometryTransformConstraintPolicy.DIRECTION_EPSILON) {
            return null;
        }

        const axisX = axisDeltaX / axisLength;
        const axisY = axisDeltaY / axisLength;
        const reflectedDirectionX = constraint.relationType === SketchRelationKind.Horizontal
            ? 2 * axisX * axisX - 1
            : 2 * axisX * axisY;
        const reflectedDirectionY = constraint.relationType === SketchRelationKind.Horizontal
            ? 2 * axisX * axisY
            : 2 * axisY * axisY - 1;

        if (Math.abs(reflectedDirectionY) <= SketchGeometryTransformConstraintPolicy.DIRECTION_EPSILON) {
            return SketchRelationKind.Horizontal;
        }
        if (Math.abs(reflectedDirectionX) <= SketchGeometryTransformConstraintPolicy.DIRECTION_EPSILON) {
            return SketchRelationKind.Vertical;
        }
        return null;
    }

    /**
     * 判断约束是否能在反射副本上原样克隆。
     *
     * @param constraint 源实体约束。
     * @param referencePlane 当前草图参考面。
     * @param axisGeometry 本次镜像轴世界几何。
     * @returns 原关系类型在反射后仍成立时返回 true。
     */
    public shouldCloneDirectlyForReflection(
        constraint: SketchRelationDefinition<ISketchObject>,
        referencePlane: ReferencePlane,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): boolean {
        if (
            constraint.relationType === SketchRelationKind.Fix
            || constraint.relationType === SketchRelationKind.FixSlot
        ) {
            return false;
        }
        return this.resolveReflectedRelationKind(constraint, referencePlane, axisGeometry)
            === constraint.relationType;
    }

    /**
     * 判断原位反射前是否需要迁出关系。
     *
     * @param constraint 待判断约束。
     * @param selectedObjectIds 当前变换域对象 UUID 集合。
     * @param referencePlane 当前草图参考面。
     * @param axisGeometry 本次镜像轴世界几何。
     * @returns 选择域内关系在反射后需要换型或删除时返回 true。
     */
    public shouldDetachBeforeReflection(
        constraint: SketchRelationDefinition<ISketchObject>,
        selectedObjectIds: ReadonlySet<string>,
        referencePlane: ReferencePlane,
        axisGeometry: SketchMirrorAxisResolvedGeometry,
    ): boolean {
        if (!constraint.getTargetsSnapshot().some((target) => selectedObjectIds.has(target.uuid))) {
            return false;
        }
        return this.resolveReflectedRelationKind(constraint, referencePlane, axisGeometry)
            !== constraint.relationType;
    }
}
